import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, ClipboardList, Factory, Truck, Users2, Package,
  UserCog, FileBarChart2, Activity, Settings as SettingsIcon, LogOut, Menu,
  Boxes, FlaskConical, FilePlus, Tags, ClipboardCheck, MapPin
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useBranding } from "@/context/BrandingContext";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

// Phase 4: nav items now carry a `module` key (from MODULE_KEYS). Visibility is
// driven by `user.effective_permissions.modules` (seeded from role template and
// overridable per-user by super-admin). `roles` array retained as legacy fallback
// only when a user doc predates the permission model.
const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, module: "dashboard", roles: ["super_admin","admin","pfi","accounts","production","dispatch","management","operations","sales"] },
  { to: "/pfi/new", label: "New PFI", icon: FilePlus, module: "new_pfi", roles: ["super_admin","admin","pfi","operations","sales"] },
  { to: "/orders", label: "Orders / PFI", icon: ClipboardList, module: "orders", roles: ["super_admin","admin","pfi","accounts","production","dispatch","management","operations","sales"] },
  { to: "/production", label: "Production", icon: Factory, module: "production", roles: ["super_admin","admin","production","management","operations"] },
  { to: "/dispatch", label: "Dispatch", icon: Truck, module: "dispatch", roles: ["super_admin","admin","dispatch","management","operations"] },
  { to: "/fg-stock", label: "FG Stock", icon: Boxes, module: "fg_stock", roles: ["super_admin","admin","production","management","operations"] },
  { to: "/inventory/closing-stock", label: "FG Closing Stock", icon: ClipboardCheck, module: "fg_closing_stock", roles: ["super_admin","admin","operations","inventory_executive"] },
  { to: "/formulas", label: "Formulas", icon: FlaskConical, module: "formulas", roles: ["super_admin","admin","production","management","operations"] },
  { to: "/sku-master", label: "SKU Master", icon: Tags, module: "sku_master", roles: ["super_admin","admin","operations","inventory_executive"] },
  { to: "/distributors", label: "Distributors", icon: Users2, module: "distributors", roles: ["super_admin","admin","pfi","accounts","management","operations","sales"] },
  { to: "/products", label: "Products", icon: Package, module: "products", roles: ["super_admin","admin","pfi","management","operations","sales"] },
  { to: "/users", label: "Users", icon: UserCog, module: "users", roles: ["super_admin"] },
  { to: "/territories", label: "Territories", icon: MapPin, module: "settings", roles: ["super_admin","admin","pfi","accounts","production","dispatch","management","operations","sales"] },
  { to: "/reports", label: "Reports", icon: FileBarChart2, module: "reports", roles: ["super_admin","admin","accounts","management","operations"] },
  { to: "/activity", label: "Activity Logs", icon: Activity, module: "activity_logs", roles: ["super_admin","admin","management"] },
  { to: "/profile", label: "My Profile", icon: UserCog, module: "profile", roles: ["super_admin","admin","pfi","accounts","production","dispatch","management","operations","sales"] },
  // Settings — SUPER ADMIN ONLY. Admin no longer has Settings access.
  { to: "/settings", label: "Settings", icon: SettingsIcon, module: "settings", roles: ["super_admin"] },
];

function Brand() {
  const { app_logo_data, company_name } = useBranding();
  return (
    <div className="flex items-center gap-2.5 px-5 py-5 border-b border-slate-800">
      {app_logo_data ? (
        <div className="w-9 h-9 rounded-lg bg-white/95 p-1 flex items-center justify-center shadow-lg shadow-slate-950/40 overflow-hidden" data-testid="sidebar-logo">
          <img src={app_logo_data} alt={company_name} className="max-w-full max-h-full object-contain" />
        </div>
      ) : (
        <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-amber-400 to-orange-600 text-slate-950 flex items-center justify-center font-black shadow-lg shadow-orange-900/30 tracking-tighter" data-testid="sidebar-logo-fallback">X</div>
      )}
      <div className="leading-tight">
        <div className="heading font-bold text-white tracking-tight">{company_name || "XTRA-OMS"}</div>
        <div className="text-[11px] text-slate-400 tracking-wide">Order Management</div>
      </div>
    </div>
  );
}

function NavItems({ onClickItem }) {
  const { user, hasModule } = useAuth();
  // Phase 4: visibility driven by effective_permissions.modules (falls back to
  // role allow-list ONLY when the user doc has no effective_permissions — e.g.
  // during a pre-migration rollout. Backend seed migration fills this for all
  // existing users on startup, so this fallback is defensive only.)
  const visible = (n) => {
    if (user?.effective_permissions?.modules) return hasModule(n.module);
    return user && n.roles.includes(user.role);  // legacy fallback
  };
  return (
    <nav className="px-3 py-4 space-y-0.5">
      {NAV.filter(visible).map((n) => {
        const Icon = n.icon;
        return (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.to === "/"}
            onClick={onClickItem}
            data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-lg text-[13px] transition-all ${
                isActive ? "bg-white/10 text-white font-medium" : "text-slate-400 hover:bg-white/5 hover:text-white"
              }`
            }
          >
            <Icon className="h-4 w-4" />
            <span>{n.label}</span>
          </NavLink>
        );
      })}
    </nav>
  );
}

export default function Shell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex lg:flex-col w-64 bg-slate-900 border-r border-slate-800 sticky top-0 h-screen">
        <Brand />
        <div className="flex-1 overflow-y-auto">
          <NavItems />
        </div>
        <div className="p-3 border-t border-slate-800">
          <div className="px-3 py-2 text-xs text-slate-400">{user?.email}</div>
          <Button
            data-testid="logout-btn"
            variant="ghost"
            className="w-full justify-start text-slate-300 hover:bg-slate-800 hover:text-white"
            onClick={async () => { await logout(); navigate("/login"); }}
          >
            <LogOut className="h-4 w-4 mr-2" /> Logout
          </Button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <header className="h-14 bg-white/80 backdrop-blur-md border-b border-slate-200 flex items-center px-4 lg:px-6 gap-3 sticky top-0 z-20">
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="lg:hidden" data-testid="mobile-menu-btn"><Menu className="h-5 w-5" /></Button>
            </SheetTrigger>
            <SheetContent side="left" className="p-0 w-64 bg-slate-900 border-slate-800">
              <Brand />
              <NavItems onClickItem={() => {}} />
            </SheetContent>
          </Sheet>
          <div className="font-semibold heading tracking-tight text-slate-900">Operations Console</div>
          <div className="ml-auto flex items-center gap-3">
            <div className="hidden sm:block text-sm text-slate-600">{user?.name}</div>
            <span className="px-2 py-0.5 rounded-full bg-slate-900 text-white text-[10px] font-semibold uppercase tracking-wider" data-testid="current-role">{user?.role}</span>
          </div>
        </header>

        <main className="flex-1 p-4 lg:p-6 max-w-[1600px] w-full mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
